import React from "react";

import {  Box, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow  } from "@mui/material";
import { OSStatistics } from "../../../../models/osStatistics";
import { MODES } from "../ConnectionStatistic";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { CHART_BG_COLORS, CHART_BORDER_COLORS} from '../../../../constants/colors';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

export interface IOperationSystemTableProps  {
    os : OSStatistics[];
    loading: boolean;
    mode: MODES;
}

const OperatingSystemTable = ({os, loading, mode} : IOperationSystemTableProps) => { 

    const prepareChartData = () => {
        if (!os || os.length === 0) return null;
        
        const labels = os.map(item => item.operatingSystem);
        const data = os.map(item => item.count);
        
        const bgColors = labels.map((_, i) => CHART_BG_COLORS[i % CHART_BG_COLORS.length]);
        const bdColors = labels.map((_, i) => CHART_BORDER_COLORS[i % CHART_BORDER_COLORS.length]);
        
        return {
            labels,
            datasets: [
                {
                    label: 'Nombre',
                    data,
                    backgroundColor: bgColors,
                    borderColor: bdColors,
                    borderWidth: 1,
                },
            ],
        };
    };
    
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'right' as const,
                display: true
            },  
            tooltip: {
                enabled: true,
                callbacks: {
                    label: function(context: any) {
                        const label = context.label || '';
                        const value = context.raw || 0;
                        const percentage = os[context.dataIndex].percentage;
                        return `${label}: ${value} (${percentage}%)`;
                    }
                }
            },
            datalabels: {
                color: 'black',
                formatter: (value: number, context: any) => {
                    const percentage = os[context.dataIndex].percentage;
                    return percentage >= 5 ? `${percentage}%` : '';
                },
                display: (context: any) => {
                    const percentage = os[context.dataIndex].percentage;
                    return percentage >= 5;
                },
            },
        },
    };
    
    const chartData = prepareChartData();
    
    if (mode === MODES.Table) {
        return (
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow style={{ backgroundColor: '#f5f5f5'}}>
                            <TableCell colSpan={3} align="center">Système d'exploitation</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableHead>
                        <TableRow>
                            <TableCell>Système d'exploitation</TableCell>
                            <TableCell align="center">Nombre</TableCell>
                            <TableCell align="center">Pourcentage</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={2} style={{ textAlign: 'center' }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : os?.length ? (
                            os.map((statistic: OSStatistics, index: number) => (
                                <TableRow key={index}>
                                    <TableCell>{statistic.operatingSystem}</TableCell>
                                    <TableCell align="center">{statistic.count}</TableCell>
                                    <TableCell align="center">{statistic.percentage}</TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={2} style={{ textAlign: 'center' }}>
                                    Aucune donnée	
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    } else if (mode === MODES.Chart) {
        return (
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box sx={{ width: '100%', textAlign: 'center', mb: 2 }}>
                    <h3>Distribution des systèmes d'exploitation</h3>
                </Box>
                <Box sx={{ height: 400, width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    {loading ? (
                        <CircularProgress />
                    ) : os?.length ? (
                        <Box sx={{ height: '100%', width: '100%', maxWidth: 600, position: 'relative' }}>
                            {chartData && <Pie data={chartData} options={chartOptions} />}
                        </Box>
                    ) : (
                        <Box sx={{ textAlign: 'center' }}>Aucune donnée</Box>
                    )}
                </Box>
            </Box>
        );
    }
    
    return null;
    }
    
        
export default OperatingSystemTable;